import { useTheme } from '@emotion/react';
import {
  Box,
  Drawer,
  IconButton,
  styled,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { StarBorder, Star as StarFilled } from '@mui/icons-material';

import logo from 'assets/sugar_logo.png';
import {
  Bookmark,
  Clock,
  FileText,
  Gift,
  LucideBotMessageSquare,
  Search,
  ShieldCheck,
  User,
  Wand2,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import FlexBox from '../../components/flexbox/FlexBox';

const StyledSidebar = styled(Box)(({ theme, $isMobileOpen }) => ({
  width: '82px',
  height: '100vh',
  backgroundColor: '#213991',
  borderRight: '1px solid #E1E1E1',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '16px 0',
  position: 'fixed',
  left: 0,
  zIndex: 1200,
  transition: 'transform 0.3s ease-in-out',
  [theme.breakpoints.down('md')]: {
    transform: $isMobileOpen ? 'translateX(0)' : 'translateX(-100%)',
    width: '100%',
    backgroundColor: '#213991',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const MobileCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '16px',
  right: '16px',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const IconWrapper = styled(Box)(({ theme, isSelected }) => ({
  width: isSelected ? '48px' : '30px',
  height: isSelected ? '48px' : '30px',
  borderRadius: isSelected ? '12px' : '50%',
  backgroundColor: isSelected ? '#ffffff' : 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  marginBottom: isSelected ? '8px' : '0px',
  boxShadow: isSelected ? '0px 2px 6px rgba(0, 0, 0, 0.08)' : 'none',
  '& svg': {
    color: isSelected ? theme.palette.primary.darkBlue : '#ABBBF3',
    transition: 'color 0.2s ease-in-out',
  },
  '&:hover': {
    backgroundColor: isSelected ? '#ffffff' : 'transparent',
    boxShadow: isSelected ? '0px 4px 8px rgba(0, 0, 0, 0.12)' : 'none',
    '& svg': {
      color: isSelected
        ? theme.palette.primary.darkBlue
        : theme.palette.primary.darkBlue,
    },
  },
}));

const MenuItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '16px',
  cursor: 'pointer',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'row',
    width: '100%',
    padding: '12px 16px',
    borderRadius: '12px',
    marginBottom: '8px',
    '&:hover': {
      backgroundColor: theme.palette.primary.grey100,
    },
  },
  '&:hover': {
    '& .menu-text': {
      color: theme.palette.primary.darkBlue,
    },
    '& .icon-wrapper svg': {
      color: theme.palette.primary.darkBlue,
    },
  },
}));

const MenuText = styled(Typography)(({ theme, isSelected }) => ({
  fontSize: '11px',
  color: isSelected ? '#ffffff' : '#ABBBF3',
  marginBottom: '8px',
  textAlign: 'center',
  fontWeight: '600',
  transition: 'color 0.2s ease-in-out',
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    marginBottom: '0',
    marginLeft: '16px',
    textAlign: 'left',
  },
}));

const HistoryDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 220,
    padding: '16px 0',
    backgroundColor: '#F1F1F7',
    left: '82px',
    right: 'auto',
    top: 0,
    height: '100vh',
    zIndex: 1199,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      left: 0,
      top: 0,
      width: '100%',
      zIndex: 1300,
    },
  },
  '& .MuiBackdrop-root': {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
}));

const HistoryHeader = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '0 16px',
  marginBottom: '24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    padding: '0px 16px',
    marginBottom: '16px',
  },
}));

const HistoryTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.secondary,
  fontWeight: '600',
}));

const HistoryItem = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
}));

const HistoryTime = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  color: theme.palette.text.secondary,
  marginBottom: '4px',
}));

const HistoryContent = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  color: theme.palette.text.primary,
}));

export default function SideMenu({ isOpen, onToggle }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isLaptop = useMediaQuery(theme.breakpoints.down('xl'));
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState('home'); // Add state for selected menu item

  const [historyItems, setHistoryItems] = useState([
    {
      id: 1,
      timestamp: '2024-03-20 14:30',
      content: '질문 내용이 여기에 표시됩니다',
      starred: false,
      type: 'question', // Could be 'question', 'summary', 'completion', etc.
      userId: 'user123', // Backend user ID
      createdAt: '2024-03-20T14:30:00Z',
      updatedAt: '2024-03-20T14:30:00Z',
    },
    {
      id: 2,
      timestamp: '2024-03-20 14:25',
      content: '다른 질문 내용이 여기에 표시됩니다',
      starred: false,
      type: 'question',
      userId: 'user123',
      createdAt: '2024-03-20T14:25:00Z',
      updatedAt: '2024-03-20T14:25:00Z',
    },
    {
      id: 3,
      timestamp: '2024-03-20 14:20',
      content: '자동 완성 기능을 사용한 텍스트',
      starred: true,
      type: 'completion',
      userId: 'user123',
      createdAt: '2024-03-20T14:20:00Z',
      updatedAt: '2024-03-20T14:20:00Z',
    },
  ]);

  const menuItems = [
    { icon: Search, label: '홈', id: 'home' },
    { icon: LucideBotMessageSquare, label: '캐릭터 챗', id: 'character-chat' },
    { icon: Wand2, label: '자동 완성', id: 'auto-complete' },
    { icon: FileText, label: '완벽 요약', id: 'summary' },
    { icon: ShieldCheck, label: '탐지 방어', id: 'detection-defense' },
    { icon: Bookmark, label: '저장됨', id: 'saved' },
    { icon: Gift, label: '혜택', id: 'benefits' },
  ];

  const bottomMenuItems = [
    {
      icon: Clock,
      label: '최근 기록',
      id: 'history',
      onClick: () => setIsHistoryOpen(true),
    },
    { icon: User, label: '마이페이지', id: 'mypage' },
  ];

  const handleMenuItemClick = itemId => {
    setSelectedMenuItem(itemId);
  };

  const handleStarClick = itemId => e => {
    e.stopPropagation();
    setHistoryItems(prev =>
      prev.map(item =>
        item.id === itemId
          ? {
              ...item,
              starred: !item.starred,
              updatedAt: new Date().toISOString(),
            }
          : item
      )
    );
  };

  const handleHistoryItemClick = item => {
    console.log('History item clicked:', item);
  };

  return (
    <>
      <StyledSidebar $isMobileOpen={isOpen}>
        {isTablet && (
          <MobileCloseButton onClick={onToggle}>
            <X size={24} color={theme.palette.text.primary} />
          </MobileCloseButton>
        )}

        {/* Logo */}
        <NavLink to={`/naver/kmong`}>
          <img
            width={'100%'}
            src={logo}
            alt={'logo'}
            style={{
              width: '60px',
              objectFit: 'contain',
              cursor: 'pointer',
              marginTop: '20px',
              marginBottom: '20px',
              [theme.breakpoints.down('md')]: {
                width: '120px',
                marginTop: '0',
                marginBottom: '0',
              },
            }}
          />
        </NavLink>

        {/* Main Menu Items */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            [theme.breakpoints.down('md')]: {
              alignItems: 'flex-start',
            },
          }}
        >
          {menuItems.map((item, index) => (
            <MenuItem key={index} onClick={() => handleMenuItemClick(item.id)}>
              <IconWrapper
                className="icon-wrapper"
                isSelected={selectedMenuItem === item.id}
              >
                <item.icon size={20} />
              </IconWrapper>
              <MenuText
                className="menu-text"
                isSelected={selectedMenuItem === item.id}
              >
                {item.label}
              </MenuText>
            </MenuItem>
          ))}
        </Box>

        {/* Bottom Menu Items */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: 'auto',
            mb: 2,
            width: '100%',
            [theme.breakpoints.down('md')]: {
              alignItems: 'flex-start',
              borderTop: `1px solid ${theme.palette.primary.grey200}`,
              paddingTop: '20px',
            },
          }}
        >
          {bottomMenuItems.map((item, index) => (
            <MenuItem
              key={index}
              onClick={item.onClick || (() => handleMenuItemClick(item.id))}
            >
              <IconWrapper
                className="icon-wrapper"
                isSelected={selectedMenuItem === item.id}
              >
                <item.icon size={20} />
              </IconWrapper>
              <MenuText
                className="menu-text"
                isSelected={selectedMenuItem === item.id}
              >
                {item.label}
              </MenuText>
            </MenuItem>
          ))}
        </Box>
      </StyledSidebar>

      <HistoryDrawer
        variant="persistent"
        anchor="left"
        open={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
      >
        <HistoryHeader>
          <HistoryTitle
            sx={{
              marginTop: '60px',
              [theme.breakpoints.down('md')]: {
                marginTop: '14px',
              },
            }}
          >
            최근 기록
          </HistoryTitle>
          <IconButton
            onClick={() => setIsHistoryOpen(false)}
            sx={{
              color: theme.palette.text.primary,
              position: 'absolute',
              right: '16px',
              top: '70px',
              transform: 'translateY(-50%)',
              [theme.breakpoints.down('md')]: {
                top: '0px',
                transform: 'none',
              },
            }}
          >
            <X size={20} />
          </IconButton>
        </HistoryHeader>
        <Box sx={{ width: '100%' }}>
          {historyItems.map(item => (
            <FlexBox
              key={item.id}
              sx={{
                padding: '0 16px',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Box
                onClick={handleStarClick(item.id)}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              >
                {item.starred ? (
                  <StarFilled
                    sx={{
                      fontSize: 22,
                      color: theme.palette.primary.orange,
                    }}
                  />
                ) : (
                  <StarBorder
                    sx={{
                      fontSize: 22,
                      color: theme.palette.primary.orange,
                    }}
                  />
                )}
              </Box>
              <HistoryItem onClick={() => handleHistoryItemClick(item)}>
                <HistoryTime>{item.timestamp}</HistoryTime>
                <HistoryContent>{item.content}</HistoryContent>
              </HistoryItem>
            </FlexBox>
          ))}
        </Box>
      </HistoryDrawer>
    </>
  );
}
